import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiPlusCircle, FiEdit, FiTrash2, FiRefreshCcw, FiDownload } from 'react-icons/fi';
import { useStore } from '../context/StoreContext';
import { useAuth } from '../context/AuthContext';
import invoiceService from '../services/invoiceService';
import supplierService from '../services/supplierService';
import purchaseOrderService from '../services/purchaseOrderService';
import inventoryService from '../services/inventoryService';
import InvoiceForm from '../components/invoices/InvoiceForm';
import useStoreTimezone from '../hooks/useStoreTimezone';
import { formatInStoreTimezone } from '../utils/dateUtils';
import { isWebApp } from '../utils/platform';

const statusBadges = {
  Unpaid: 'bg-yellow-100 text-yellow-800',
  Scheduled: 'bg-blue-100 text-blue-700',
  Paid: 'bg-green-100 text-green-700',
};

const invoiceSorters = {
  invoiceNumber: (invoice) => invoice.invoiceNumber || '',
  supplier: (invoice) => invoice.supplier?.name || '',
  status: (invoice) => invoice.status || '',
  invoiceDate: (invoice) => (invoice.invoiceDate ? new Date(invoice.invoiceDate).getTime() : null),
  dueDate: (invoice) => (invoice.dueDate ? new Date(invoice.dueDate).getTime() : null),
  scheduledPaymentDate: (invoice) =>
    invoice.scheduledPaymentDate ? new Date(invoice.scheduledPaymentDate).getTime() : null,
  paidDate: (invoice) => (invoice.paidDate ? new Date(invoice.paidDate).getTime() : null),
  poNumber: (invoice) => invoice.purchaseOrder?.poNumber || '',
  totalAmount: (invoice) => Number(invoice.totalAmount || 0),
  supplierInvoiceAmount: (invoice) =>
    invoice.supplierInvoiceAmount != null ? Number(invoice.supplierInvoiceAmount) : null,
};

const compareSortValues = (left, right, direction) => {
  const factor = direction === 'asc' ? 1 : -1;
  if (left == null && right == null) return 0;
  if (left == null) return 1 * factor;
  if (right == null) return -1 * factor;
  if (typeof left === 'number' && typeof right === 'number') {
    return (left - right) * factor;
  }
  return String(left).localeCompare(String(right), undefined, {
    numeric: true,
    sensitivity: 'base',
  }) * factor;
};

const InvoicesPage = () => {
  const { activeStore } = useStore();
  const { user } = useAuth();
  const timezone = useStoreTimezone();
  const [invoices, setInvoices] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0, limit: 25 });
  const [pageSize, setPageSize] = useState(25);
  const [filters, setFilters] = useState({
    status: '',
    supplierId: '',
    invoiceNumber: '',
    invoiceStartDate: '',
    invoiceEndDate: '',
    dueStartDate: '',
    dueEndDate: '',
  });
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [invoiceSort, setInvoiceSort] = useState({ key: '', direction: 'asc' });
  const [suppliers, setSuppliers] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [modalError, setModalError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const canRead = user?.permissions?.includes('invoices:read');
  const canCreate = user?.permissions?.includes('invoices:create');
  const canUpdate = user?.permissions?.includes('invoices:update');
  const canDelete = user?.permissions?.includes('invoices:delete');

  const activeStoreId = activeStore?._id;

  const loadSuppliers = useCallback(async () => {
    try {
      const response = await supplierService.fetchSuppliers({ limit: 200, compact: true });
      setSuppliers(response.data || []);
    } catch (err) {
      // Non-blocking.
    }
  }, []);

  const loadPurchaseOrders = useCallback(async () => {
    if (!activeStoreId) return;
    try {
      const response = await purchaseOrderService.fetchPurchaseOrders({
        storeId: activeStoreId,
        limit: 200,
      });
      setPurchaseOrders(response.data || []);
    } catch (err) {
      // Non-blocking.
    }
  }, [activeStoreId]);

  const loadProducts = useCallback(async () => {
    if (!activeStoreId) return;
    try {
      let page = 1;
      let totalPages = 1;
      let aggregatedProducts = [];
      do {
        const response = await inventoryService.fetchProducts(activeStoreId, { page, limit: 100 });
        const items = response?.data || [];
        aggregatedProducts = aggregatedProducts.concat(items);
        totalPages = response?.pagination?.pages || 1;
        page += 1;
      } while (page <= totalPages);
      setProducts(aggregatedProducts);
    } catch (err) {
      // Non-blocking.
    }
  }, [activeStoreId]);

  const loadInvoices = useCallback(
    async (page = 1, limit = pageSize) => {
      if (!activeStoreId || !canRead) return;
      setLoading(true);
      setError('');
      try {
        const response = await invoiceService.fetchInvoices({
          storeId: activeStoreId,
          status: filters.status || undefined,
          supplierId: filters.supplierId || undefined,
          invoiceNumber: filters.invoiceNumber || undefined,
          invoiceStartDate: filters.invoiceStartDate || undefined,
          invoiceEndDate: filters.invoiceEndDate || undefined,
          dueStartDate: filters.dueStartDate || undefined,
          dueEndDate: filters.dueEndDate || undefined,
          overdueOnly: overdueOnly || undefined,
          page,
          limit,
        });
        setInvoices(response.data || []);
        setPagination(
          response.pagination || {
            page,
            pages: 1,
            total: response.data?.length || 0,
            limit,
          }
        );
      } catch (err) {
        setError(err.response?.data?.message || 'Unable to fetch invoices.');
      } finally {
        setLoading(false);
      }
    },
    [activeStoreId, canRead, filters, overdueOnly, pageSize]
  );

  useEffect(() => {
    if (canRead) {
      loadSuppliers();
      loadPurchaseOrders();
      loadProducts();
    }
  }, [canRead, loadSuppliers, loadPurchaseOrders, loadProducts]);

  useEffect(() => {
    if (!activeStoreId) return;
    loadInvoices(1, pageSize);
  }, [activeStoreId, filters, overdueOnly, pageSize, loadInvoices]);

  const handleOpenModal = async (invoice = null) => {
    if (invoice && !canUpdate) {
      setError('You do not have permission to update invoices.');
      return;
    }
    if (!invoice && !canCreate) {
      setError('You do not have permission to create invoices.');
      return;
    }
    setModalError('');
    if (invoice?._id) {
      try {
        const detailed = await invoiceService.getInvoice(invoice._id);
        setSelectedInvoice(detailed);
      } catch (err) {
        setError(err.response?.data?.message || 'Unable to load invoice details.');
        return;
      }
    } else {
      setSelectedInvoice(null);
    }
    setModalOpen(true);
  };

  const handleSaveInvoice = async (payload) => {
    if (!activeStoreId) {
      setModalError('Select a store to save invoices.');
      return;
    }
    try {
      if (selectedInvoice?._id) {
        await invoiceService.updateInvoice(selectedInvoice._id, payload);
      } else {
        await invoiceService.createInvoice({ ...payload, store: activeStoreId });
      }
      setModalOpen(false);
      setSelectedInvoice(null);
      setModalError('');
      loadInvoices(pagination.page);
    } catch (err) {
      setModalError(err.response?.data?.message || 'Unable to save invoice.');
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!canDelete) {
      setError('You do not have permission to delete invoices.');
      return;
    }
    if (!window.confirm('Delete this invoice?')) {
      return;
    }
    try {
      await invoiceService.deleteInvoice(invoiceId);
      loadInvoices(pagination.page);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to delete invoice.');
    }
  };

  const handleAddNote = async (note) => {
    if (!selectedInvoice?._id) return null;
    const updated = await invoiceService.addInvoiceNote(selectedInvoice._id, note);
    setSelectedInvoice(updated);
    return updated;
  };

  const handleExportCsv = async () => {
    if (!activeStoreId || exporting) return;
    setExporting(true);
    setError('');
    try {
      const maxLimit = 200;
      const baseParams = {
        storeId: activeStoreId,
        status: filters.status || undefined,
        supplierId: filters.supplierId || undefined,
        invoiceNumber: filters.invoiceNumber || undefined,
        invoiceStartDate: filters.invoiceStartDate || undefined,
        invoiceEndDate: filters.invoiceEndDate || undefined,
        dueStartDate: filters.dueStartDate || undefined,
        dueEndDate: filters.dueEndDate || undefined,
        overdueOnly: overdueOnly || undefined,
      };
      const firstResponse = await invoiceService.fetchInvoices({
        ...baseParams,
        page: 1,
        limit: maxLimit,
      });
      const allInvoices = [...(firstResponse.data || [])];
      const totalPages = firstResponse.pagination?.pages || 1;
      if (totalPages > 1) {
        const remainingPages = Array.from({ length: totalPages - 1 }, (_, index) => index + 2);
        const responses = await Promise.all(
          remainingPages.map((nextPage) =>
            invoiceService.fetchInvoices({ ...baseParams, page: nextPage, limit: maxLimit })
          )
        );
        responses.forEach((response) => {
          allInvoices.push(...(response.data || []));
        });
      }

      const headers = [
        'Invoice Number',
        'Supplier',
        'Status',
        'Invoice Date',
        'Due Date',
        'Scheduled Payment Date',
        'Paid Date',
        'Calculated Amount',
        'Supplier Invoice Amount',
        'Purchase Order',
        'Overdue',
      ];

      const escapeCsv = (value) => {
        if (value === null || value === undefined) return '';
        const stringValue = String(value);
        if (/[",\n]/.test(stringValue)) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      };

      const exportInvoices = overdueOnly ? allInvoices.filter((invoice) => invoice.isOverdue) : allInvoices;
      const rows = exportInvoices.map((inv) => [
        escapeCsv(inv.invoiceNumber),
        escapeCsv(inv.supplier?.name || ''),
        escapeCsv(inv.status),
        escapeCsv(formatInStoreTimezone(inv.invoiceDate, 'dd-MM-yyyy', timezone)),
        escapeCsv(formatInStoreTimezone(inv.dueDate, 'dd-MM-yyyy', timezone)),
        escapeCsv(formatInStoreTimezone(inv.scheduledPaymentDate, 'dd-MM-yyyy', timezone)),
        escapeCsv(formatInStoreTimezone(inv.paidDate, 'dd-MM-yyyy', timezone)),
        escapeCsv(Number(inv.totalAmount || 0).toFixed(2)),
        escapeCsv(inv.supplierInvoiceAmount != null ? Number(inv.supplierInvoiceAmount).toFixed(2) : ''),
        escapeCsv(inv.purchaseOrder?.poNumber || ''),
        escapeCsv(inv.isOverdue ? 'Yes' : 'No'),
      ]);

      const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const safeStore = (activeStore?.name || 'store').replace(/[^a-z0-9-_]+/gi, '-');
      const timestamp = new Date().toISOString().slice(0, 10);
      link.href = url;
      link.setAttribute('download', `invoices-${safeStore}-${timestamp}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to export invoices.');
    } finally {
      setExporting(false);
    }
  };

  const availablePurchaseOrders = useMemo(
    () =>
      purchaseOrders.filter((order) => {
        if (!order) return false;
        if (!order.invoiceId) return true;
        if (selectedInvoice?._id && String(order.invoiceId) === String(selectedInvoice._id)) {
          return true;
        }
        return false;
      }),
    [purchaseOrders, selectedInvoice]
  );

  const toggleSort = useCallback((key) => {
    setInvoiceSort((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  }, []);

  const handleStatusFilterChange = useCallback((event) => {
    const nextStatus = event.target.value;
    setFilters((prev) => ({ ...prev, status: nextStatus }));
    if (nextStatus === 'Paid') {
      setOverdueOnly(false);
    }
  }, []);

  const handleOverdueOnlyChange = useCallback((event) => {
    const checked = event.target.checked;
    setOverdueOnly(checked);
    if (checked) {
      setFilters((prev) => (prev.status === 'Paid' ? { ...prev, status: '' } : prev));
    }
  }, []);

  const filteredInvoices = useMemo(() => invoices, [invoices]);

  const visibleInvoices = useMemo(() => {
    if (!invoiceSort.key) return filteredInvoices;
    const sorter = invoiceSorters[invoiceSort.key];
    if (!sorter) return filteredInvoices;
    return [...filteredInvoices].sort((left, right) =>
      compareSortValues(sorter(left), sorter(right), invoiceSort.direction)
    );
  }, [filteredInvoices, invoiceSort]);

  const currentPage = Math.max(1, Number(pagination.page || 1));
  const totalPages = Math.max(1, Number(pagination.pages || 1));
  const currentLimit = Math.max(1, Number(pagination.limit || pageSize || 25));
  const totalInvoices = Number(pagination.total || 0);
  const visibleStart = visibleInvoices.length ? ((currentPage - 1) * currentLimit) + 1 : 0;
  const visibleEnd = visibleInvoices.length ? visibleStart + visibleInvoices.length - 1 : 0;

  if (!canRead) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-red-600">Access Denied</h1>
        <p className="mt-2">You do not have the required permissions to view invoices.</p>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Invoices</h1>
          <p className="text-gray-500">Track supplier invoices and payment status for this store.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => loadInvoices(currentPage, currentLimit)}
            className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 inline-flex items-center"
          >
            <FiRefreshCcw className="mr-2" /> Refresh
          </button>
          {isWebApp() && (
            <button
              onClick={handleExportCsv}
              disabled={exporting || loading}
              className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 inline-flex items-center disabled:opacity-50"
            >
              <FiDownload className="mr-2" /> {exporting ? 'Exporting...' : 'Export CSV'}
            </button>
          )}
          {canCreate && (
            <button
              onClick={() => handleOpenModal(null)}
              className="inline-flex items-center bg-primary text-white px-4 py-2 rounded-md shadow hover:bg-primary-dark transition"
            >
              <FiPlusCircle className="mr-2" />
              New Invoice
            </button>
          )}
        </div>
      </div>

      <div className="bg-white p-4 rounded-lg shadow mb-6 grid grid-cols-1 md:grid-cols-6 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Status</label>
          <select
            value={filters.status}
            onChange={handleStatusFilterChange}
            className="mt-1 w-full border-gray-300 rounded-md shadow-sm"
          >
            <option value="">All statuses</option>
            <option value="Unpaid">Unpaid</option>
            <option value="Scheduled">Scheduled</option>
            <option value="Paid" disabled={overdueOnly}>Paid</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Supplier</label>
          <select
            value={filters.supplierId}
            onChange={(e) => setFilters((prev) => ({ ...prev, supplierId: e.target.value }))}
            className="mt-1 w-full border-gray-300 rounded-md shadow-sm"
          >
            <option value="">All suppliers</option>
            {suppliers.map((supplier) => (
              <option key={supplier._id} value={supplier._id}>
                {supplier.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Invoice Number</label>
          <input
            value={filters.invoiceNumber}
            onChange={(e) => setFilters((prev) => ({ ...prev, invoiceNumber: e.target.value }))}
            className="mt-1 w-full border-gray-300 rounded-md shadow-sm"
            placeholder="Search invoice #"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Invoice Date From</label>
          <input
            type="date"
            value={filters.invoiceStartDate}
            onChange={(e) => setFilters((prev) => ({ ...prev, invoiceStartDate: e.target.value }))}
            className="mt-1 w-full border-gray-300 rounded-md shadow-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Invoice Date To</label>
          <input
            type="date"
            value={filters.invoiceEndDate}
            onChange={(e) => setFilters((prev) => ({ ...prev, invoiceEndDate: e.target.value }))}
            className="mt-1 w-full border-gray-300 rounded-md shadow-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Due Date From</label>
          <input
            type="date"
            value={filters.dueStartDate}
            onChange={(e) => setFilters((prev) => ({ ...prev, dueStartDate: e.target.value }))}
            className="mt-1 w-full border-gray-300 rounded-md shadow-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Due Date To</label>
          <input
            type="date"
            value={filters.dueEndDate}
            onChange={(e) => setFilters((prev) => ({ ...prev, dueEndDate: e.target.value }))}
            className="mt-1 w-full border-gray-300 rounded-md shadow-sm"
          />
        </div>
        <div className="flex items-center gap-2 md:col-span-2 mt-6">
          <input
            id="overdue-only"
            type="checkbox"
            checked={overdueOnly}
            onChange={handleOverdueOnlyChange}
            className="h-4 w-4 text-primary border-gray-300 rounded"
          />
          <label htmlFor="overdue-only" className="text-sm font-medium text-gray-700">
            Overdue only
          </label>
        </div>
      </div>

      {error && <p className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</p>}

      <div className="bg-white shadow-md rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="px-6 py-3 text-left">
                <button
                  type="button"
                  onClick={() => toggleSort('invoiceNumber')}
                  className="inline-flex items-center gap-1"
                >
                  <span>Invoice</span>
                  {invoiceSort.key === 'invoiceNumber' && (
                    <span className="text-xs text-gray-400">
                      {invoiceSort.direction === 'asc' ? '^' : 'v'}
                    </span>
                  )}
                </button>
              </th>
              <th className="px-6 py-3 text-left">
                <button
                  type="button"
                  onClick={() => toggleSort('supplier')}
                  className="inline-flex items-center gap-1"
                >
                  <span>Supplier</span>
                  {invoiceSort.key === 'supplier' && (
                    <span className="text-xs text-gray-400">
                      {invoiceSort.direction === 'asc' ? '^' : 'v'}
                    </span>
                  )}
                </button>
              </th>
              <th className="px-6 py-3 text-left">
                <button
                  type="button"
                  onClick={() => toggleSort('status')}
                  className="inline-flex items-center gap-1"
                >
                  <span>Status</span>
                  {invoiceSort.key === 'status' && (
                    <span className="text-xs text-gray-400">
                      {invoiceSort.direction === 'asc' ? '^' : 'v'}
                    </span>
                  )}
                </button>
              </th>
              <th className="px-6 py-3 text-left">
                <button
                  type="button"
                  onClick={() => toggleSort('invoiceDate')}
                  className="inline-flex items-center gap-1"
                >
                  <span>Invoice Date</span>
                  {invoiceSort.key === 'invoiceDate' && (
                    <span className="text-xs text-gray-400">
                      {invoiceSort.direction === 'asc' ? '^' : 'v'}
                    </span>
                  )}
                </button>
              </th>
              <th className="px-6 py-3 text-left">
                <button
                  type="button"
                  onClick={() => toggleSort('dueDate')}
                  className="inline-flex items-center gap-1"
                >
                  <span>Due Date</span>
                  {invoiceSort.key === 'dueDate' && (
                    <span className="text-xs text-gray-400">
                      {invoiceSort.direction === 'asc' ? '^' : 'v'}
                    </span>
                  )}
                </button>
              </th>
              <th className="px-6 py-3 text-left">
                <button
                  type="button"
                  onClick={() => toggleSort('scheduledPaymentDate')}
                  className="inline-flex items-center gap-1"
                >
                  <span>Scheduled Date</span>
                  {invoiceSort.key === 'scheduledPaymentDate' && (
                    <span className="text-xs text-gray-400">
                      {invoiceSort.direction === 'asc' ? '^' : 'v'}
                    </span>
                  )}
                </button>
              </th>
              <th className="px-6 py-3 text-left">
                <button
                  type="button"
                  onClick={() => toggleSort('paidDate')}
                  className="inline-flex items-center gap-1"
                >
                  <span>Paid Date</span>
                  {invoiceSort.key === 'paidDate' && (
                    <span className="text-xs text-gray-400">
                      {invoiceSort.direction === 'asc' ? '^' : 'v'}
                    </span>
                  )}
                </button>
              </th>
              <th className="px-6 py-3 text-left">
                <button
                  type="button"
                  onClick={() => toggleSort('poNumber')}
                  className="inline-flex items-center gap-1"
                >
                  <span>PO</span>
                  {invoiceSort.key === 'poNumber' && (
                    <span className="text-xs text-gray-400">
                      {invoiceSort.direction === 'asc' ? '^' : 'v'}
                    </span>
                  )}
                </button>
              </th>
              <th className="px-6 py-3 text-right">
                <button
                  type="button"
                  onClick={() => toggleSort('totalAmount')}
                  className="inline-flex items-center gap-1 w-full justify-end"
                >
                  <span>Calculated</span>
                  {invoiceSort.key === 'totalAmount' && (
                    <span className="text-xs text-gray-400">
                      {invoiceSort.direction === 'asc' ? '^' : 'v'}
                    </span>
                  )}
                </button>
              </th>
              <th className="px-6 py-3 text-right">
                <button
                  type="button"
                  onClick={() => toggleSort('supplierInvoiceAmount')}
                  className="inline-flex items-center gap-1 w-full justify-end"
                >
                  <span>Supplier Amount</span>
                  {invoiceSort.key === 'supplierInvoiceAmount' && (
                    <span className="text-xs text-gray-400">
                      {invoiceSort.direction === 'asc' ? '^' : 'v'}
                    </span>
                  )}
                </button>
              </th>
              <th className="px-6 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {visibleInvoices.length === 0 && !loading && (
              <tr>
                <td colSpan="11" className="text-center py-6 text-gray-500">
                  No invoices found.
                </td>
              </tr>
            )}
            {visibleInvoices.map((invoice) => {
              const statusStyle = statusBadges[invoice.status] || 'bg-gray-100 text-gray-700';
              const overdueRow = invoice.isOverdue ? 'bg-red-50' : '';
              return (
                <tr key={invoice._id} className={overdueRow}>
                  <td className="px-6 py-4 text-left">
                    <button
                      type="button"
                      onClick={() => handleOpenModal(invoice)}
                      className="text-primary hover:text-primary-dark hover:underline"
                    >
                      {invoice.invoiceNumber || 'Invoice'}
                    </button>
                    {invoice.isOverdue && (
                      <span className="ml-2 text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full">
                        Overdue
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">{invoice.supplier?.name || '-'}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusStyle}`}>
                      {invoice.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {formatInStoreTimezone(invoice.invoiceDate, 'dd-MM-yyyy', timezone) || '-'}
                  </td>
                  <td className="px-6 py-4">
                    {formatInStoreTimezone(invoice.dueDate, 'dd-MM-yyyy', timezone) || '-'}
                  </td>
                  <td className="px-6 py-4">
                    {formatInStoreTimezone(invoice.scheduledPaymentDate, 'dd-MM-yyyy', timezone) || '-'}
                  </td>
                  <td className="px-6 py-4">
                    {formatInStoreTimezone(invoice.paidDate, 'dd-MM-yyyy', timezone) || '-'}
                  </td>
                  <td className="px-6 py-4">{invoice.purchaseOrder?.poNumber || '-'}</td>
                  <td className="px-6 py-4 text-right">
                    ${Number(invoice.totalAmount || 0).toFixed(2)}
                  </td>
                  <td className="px-6 py-4 text-right">
                    {invoice.supplierInvoiceAmount != null
                      ? `$${Number(invoice.supplierInvoiceAmount).toFixed(2)}`
                      : '-'}
                  </td>
                  <td className="px-6 py-4 text-right space-x-2">
                    {canUpdate && (
                      <button
                        type="button"
                        onClick={() => handleOpenModal(invoice)}
                        className="text-indigo-600 hover:text-indigo-900"
                      >
                        <FiEdit />
                      </button>
                    )}
                    {canDelete && (
                      <button
                        type="button"
                        onClick={() => handleDeleteInvoice(invoice._id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <FiTrash2 />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="flex flex-col gap-3 border-t px-6 py-4 text-sm text-gray-600 md:flex-row md:items-center md:justify-between">
          <span>
            Showing {visibleStart}-{visibleEnd} of {totalInvoices} invoices
          </span>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <label className="flex items-center gap-2">
              <span>Rows</span>
              <select
                value={currentLimit}
                onChange={(event) => setPageSize(Number(event.target.value) || 25)}
                className="rounded border border-gray-300 px-2 py-1"
              >
                {[25, 50, 100].map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => loadInvoices(currentPage - 1, currentLimit)}
                disabled={loading || currentPage <= 1}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Previous
              </button>
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() => loadInvoices(currentPage + 1, currentLimit)}
                disabled={loading || currentPage >= totalPages}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-3 backdrop-blur-[1px] md:p-6">
          <div className="w-full max-w-[96rem] max-h-[calc(100vh-1.5rem)] md:max-h-[calc(100vh-3rem)] flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
            <div className="border-b border-dashed border-gray-300 px-6 py-4">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500">Invoices</p>
              <h2 className="mt-1 text-2xl font-bold text-gray-900">
                {selectedInvoice ? 'Edit Invoice' : 'Create Invoice'}
              </h2>
              <p className="text-sm text-gray-500">Review details in document format before saving.</p>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-4 sm:px-6 pb-6 pt-5">
              {modalError && <p className="mb-4 rounded border border-red-200 bg-red-50 p-3 text-red-700">{modalError}</p>}
              <InvoiceForm
                invoice={selectedInvoice}
                suppliers={suppliers}
                purchaseOrders={availablePurchaseOrders}
                products={products}
                defaultStoreId={activeStoreId}
                defaultStoreName={activeStore?.name}
                onSave={handleSaveInvoice}
                onCancel={() => {
                  setModalOpen(false);
                  setSelectedInvoice(null);
                  setModalError('');
                }}
                onAddNote={handleAddNote}
                onLoadPurchaseOrder={purchaseOrderService.getPurchaseOrder}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoicesPage;
